import type { SectionKey } from './models'

export type RetrievalRequest = {
  readonly scope: 'reviewed' | 'sources' | 'candidates'
  readonly source_ids: readonly string[]
}

export type CitationAnchor = {
  readonly start_char: number
  readonly end_char: number
  readonly text: string
}

export type CitationSourceSpan = {
  readonly source_id: string
  readonly source_version_id: string
  readonly start_char: number
  readonly end_char: number
  readonly page_number?: number | null
}

export type MemoryReference =
  | { readonly kind: 'source'; readonly source: CitationSourceSpan }
  | {
      readonly kind: 'evidence'
      readonly evidence_id: string
      readonly evidence_revision: number
      readonly source: CitationSourceSpan
    }
  | { readonly kind: 'signal'; readonly signal_id: string; readonly signal_revision: number }

export type CitationAttribution = {
  readonly speaker: string | null
  readonly role: string | null
  readonly organization: string | null
  readonly occurred_on: string | null
  readonly origin: 'source_text' | 'upload_metadata'
}

export type CitationSignalEdge = CitationSourceSpan & {
  readonly evidence_id: string
  readonly evidence_revision: number
  readonly stance: 'supports' | 'challenges'
  readonly rationale: string
  readonly observation: string
  readonly excerpt: string
}

type SnapshotMetadata = Readonly<Record<string, string | number | boolean | null>>
export type CitationSnapshot =
  | {
      readonly kind: 'source'
      readonly excerpt: string
      readonly attribution: CitationAttribution | null
      readonly metadata: SnapshotMetadata
    }
  | {
      readonly kind: 'evidence'
      readonly observation: string
      readonly excerpt: string
      readonly rationale: string
      readonly attribution: CitationAttribution | null
      readonly source_version_id: string
      readonly start_char: number
      readonly end_char: number
      readonly page_number?: number | null
      readonly metadata: SnapshotMetadata
    }
  | {
      readonly kind: 'signal'
      readonly title: string
      readonly explanation: string
      readonly edges: readonly CitationSignalEdge[]
      readonly metadata: SnapshotMetadata
    }

export type Citation = {
  readonly schema_version: 1
  readonly id: string
  readonly anchor: CitationAnchor
  readonly retrieval_handle: string | null
  readonly reference: MemoryReference
  readonly snapshot: CitationSnapshot
  readonly public_state: 'linked' | 'unavailable'
  readonly review_state: 'reviewed' | 'review_required'
}

export type ClaimRead = {
  readonly claim_id: string
  readonly section: SectionKey
  readonly anchor: CitationAnchor
  readonly anchor_state: 'anchored' | 'needs_reanchor'
  readonly citations: readonly Citation[]
}

export type ClaimAnchorInput = CitationAnchor & { readonly section: SectionKey }
export type CitationInput =
  | (CitationSourceSpan & { readonly kind: 'source'; readonly confirm_draft_use: boolean })
  | {
      readonly kind: 'evidence'
      readonly evidence_id: string
      readonly evidence_revision: number
    }
  | { readonly kind: 'signal'; readonly signal_id: string; readonly signal_revision: number }

export type ClaimCreate = ClaimAnchorInput & {
  readonly expected_revision: number
  readonly citations: readonly CitationInput[]
}
export type ClaimUpdate = {
  readonly expected_revision: number
  readonly anchor?: ClaimAnchorInput
  readonly citations?: readonly CitationInput[]
}

export type CitationBlocker = {
  readonly code: 'citation_needs_review' | 'citation_anchor_stale' | 'citation_inaccessible'
  readonly claim_id: string
}

export function codePointSlice(text: string, start: number, end: number): string {
  return Array.from(text).slice(start, end).join('')
}

export function anchorMatches(text: string, anchor: CitationAnchor): boolean {
  return (
    Number.isInteger(anchor.start_char) &&
    Number.isInteger(anchor.end_char) &&
    anchor.start_char >= 0 &&
    anchor.end_char > anchor.start_char &&
    Array.from(anchor.text).length === anchor.end_char - anchor.start_char &&
    codePointSlice(text, anchor.start_char, anchor.end_char) === anchor.text
  )
}

export function anchorFromSelection(
  text: string,
  start: number,
  end: number,
): CitationAnchor | null {
  const selected = text.slice(start, end)
  if (!selected || start < 0 || end > text.length) return null
  const anchor = {
    start_char: Array.from(text.slice(0, start)).length,
    end_char: Array.from(text.slice(0, end)).length,
    text: selected,
  }
  return anchorMatches(text, anchor) ? anchor : null
}

export function memoryRecordHref(
  workspaceId: string,
  kind: 'source' | 'evidence' | 'signal',
  id: string,
  options: { revision?: number; version?: string; start?: number; end?: number } = {},
): string {
  const query = new URLSearchParams({
    view: kind === 'source' ? 'sources' : kind === 'signal' ? 'signals' : 'evidence',
    record: `${kind}:${id}`,
  })
  if (options.revision !== undefined) query.set('revision', String(options.revision))
  if (options.version) query.set('version', options.version)
  if (options.start !== undefined) query.set('start', String(options.start))
  if (options.end !== undefined) query.set('end', String(options.end))
  return `/w/${encodeURIComponent(workspaceId)}/memory?${query}`
}

export function citationHref(workspaceId: string, reference: MemoryReference): string {
  if (reference.kind === 'signal')
    return memoryRecordHref(workspaceId, 'signal', reference.signal_id, {
      revision: reference.signal_revision,
    })
  const span = reference.source
  return memoryRecordHref(
    workspaceId,
    reference.kind,
    reference.kind === 'evidence' ? reference.evidence_id : span.source_id,
    {
      ...(reference.kind === 'evidence' ? { revision: reference.evidence_revision } : {}),
      version: span.source_version_id,
      start: span.start_char,
      end: span.end_char,
    },
  )
}
